import React, { useState, useEffect } from "react";
import get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";
import { parseUrl } from "query-string";
import { login, currentUser, oauthAuthorize } from "@quintype/bridgekeeper-js";
import { AccessType } from "@quintype/components";
import { MEMBER_UPDATED, SUBSCRIBER_METADATA_UPDATED } from "../../../store/actions";
import { STATIC_KEYWORDS } from "../../../../constants";
import { Icon } from "../../../atoms/icon";
import { ResetPasswordModal } from "../../../molecules/reset-password-modal";
import "./sign-in-modal.m.css";
import OpenEye from "../../../../../assets/icons/open-eye";
import OffEye from "../../../../../assets/icons/off-eye";

const SignInModalContent = ({ isOpen, onClose, getSubscriberMetadata, initAccessType }) => {
  const [user, setUser] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState({});
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const { SIGN_IN } = STATIC_KEYWORDS;

  const qtConfig = useSelector((state) => get(state, ["qt"], {}));
  const publisherAttributes = get(qtConfig, ["config", "publisher-attributes"], {});
  const currentPath = get(qtConfig, ["currentPath"], "");
  const clientId = get(publisherAttributes, ["sso_login", "client_id"], "");
  const ssoLoginIsEnable = get(publisherAttributes, ["sso_login", "is_enable"], false);

  const dispatch = useDispatch();

  // Handle modal open/close effects
  useEffect(() => {
    if (isOpen) {
      // Add modal-open class to body
      document.body.classList.add('modal-open');

      // Disable body scroll
      document.body.style.overflow = 'hidden';
    }

    // Cleanup function
    return () => {
      // Remove modal-open class from body
      document.body.classList.remove('modal-open');

      // Re-enable body scroll
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const getCurrentUser = async () => {
    try {
      const currentUserResp = await currentUser();
      const user = get(currentUserResp, ["user"], null);
      dispatch({ type: MEMBER_UPDATED, member: user });

      // Fetch subscriber metadata for forum-based display using AccessType
      if (user && getSubscriberMetadata && initAccessType) {
        try {
          initAccessType(() => {
            getSubscriberMetadata()
              .then((response) => {
                const metadata = get(response, ["data", "other"], null);
                if (metadata) {
                  dispatch({ type: SUBSCRIBER_METADATA_UPDATED, metadata: metadata });
                }
              });
          });
        } catch (metadataError) {
          console.log("Error fetching subscriber metadata:", metadataError);
        }
      }
    } catch (err) {
      console.log("error--------", err);
    }
  };

  const setData = (e) => {
    const userObj = { ...user };
    const fieldName = e.target.id;
    userObj[fieldName] = e.target.value;
    setUser(userObj);
    // Clear errors when user starts typing
    if (error.message) {
      setError({});
    }
  };

  const loginHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const userObj = {
      username: user.email,
      email: user.email,
      password: user.password,
    };

    if (user.email.length < 1 || user.password.length < 1) {
      setError({ message: "Please provide username and password" });
      return null;
    }

    login(userObj)
      .then(async ({ user: loggedInUser, message }) => {
        if (!loggedInUser) {
          setError({ message });
          return;
        }

        // Login successful - update user state and close modal
        await getCurrentUser();
        onClose();

        // Handle OAuth redirect if SSO is enabled
        const params = parseUrl(currentPath);
        const callbackUrl = get(params, ["query", "callback_uri"]) || get(publisherAttributes, ["sso_login", "callback_Url"], "");
        const redirectUrl = get(params, ["query", "redirect_uri"]) || get(publisherAttributes, ["sso_login", "redirect_Url"], "");
        const allowAjax = true;
        const oauthResponse = ssoLoginIsEnable && (await oauthAuthorize(clientId, redirectUrl, callbackUrl, allowAjax));
        if (oauthResponse && oauthResponse.redirect_uri) {
          window.location.href = oauthResponse.redirect_uri;
        }
      })
      .catch((error) => {
        setError({ message: error.message || "Login failed" });
      });
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div styleName="modal-backdrop" onClick={handleBackdropClick}>
      <div styleName="modal-container">
        <div styleName={`modal-content ${showResetPasswordModal ? 'hidden' : ''}`}>
          <button styleName="close-button" onClick={onClose} type="button" aria-label="Close">
            <Icon name="close" size="xsmall" />
          </button>

          <div styleName="modal-header">
            <h2 styleName="modal-title">Sign In to IMA India Forum</h2>
          </div>

          <form onSubmit={loginHandler} styleName="modal-form">
            <div styleName={`form-group ${error.message ? "error-group" : ""}`}>
              <div styleName={`input-container ${error.message ? "error-input" : ""}`}>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={user.email}
                  onChange={setData}
                  placeholder="Email*"
                  styleName={`form-input`}
                  required
                />
              </div>
            </div>

            {error.message && (
              <div styleName="error-message">
                {error.message === "\"body.email\" must be a valid email" ? "Enter a valid email" : error.message}
              </div>
            )}

            <div styleName="form-group pass-container">
              <div styleName="input-container">
                {showPassword ? (
                  <input
                    type="text"
                    id="password"
                    value={user.password}
                    onChange={setData}
                    placeholder="Password*"
                    styleName="form-input"
                    required
                  />
                ) : (
                  <div styleName="password-dots-container">
                    {user.password && (
                      <div styleName="password-dots">
                        {Array.from({ length: user.password.length }).map((_, index) => (
                          <div key={index} styleName="password-dot" />
                        ))}
                      </div>
                    )}
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={user.password}
                      onChange={setData}
                      onFocus={() => setIsPasswordFocused(true)}
                      onBlur={() => setIsPasswordFocused(false)}
                      placeholder={!user.password ? "Password*" : ""}
                      styleName={`form-input ${user?.password ? 'password-hidden' : ''}`}
                      autoComplete="current-password"
                      required
                    />
                  </div>
                )}
                <button
                  type="button"
                  styleName="password-toggle"
                  disabled={!user.password}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <OffEye color="#BFBFBF" />: <OpenEye color="#BFBFBF" />}
                </button>
              </div>
            </div>

            <div styleName="forgot-password">
              <button
                type="button"
                styleName="forgot-link"
                onClick={() => setShowResetPasswordModal(true)}
              >
                Forgot Password?
              </button>
            </div>

            <button type="submit" styleName="submit-button" onClick={loginHandler}>
              {SIGN_IN}
            </button>
          </form>
        </div>
      </div>

      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
      />
    </div>
  );
};

const SignInModal = ({ isOpen, onClose }) => {
  const member = useSelector((state) => get(state, ["member"], null));
  const { key, accessTypeBkIntegrationId } = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "accesstypeConfig"], {})
  );
  const email = get(member, ["email"], "");
  const phone = get(member, ["metadata", "phone-number"], "");

  if (!isOpen) return null;

  return (
    <AccessType
      enableAccesstype={true}
      accessTypeKey={key}
      email={email}
      phone={phone}
      accessTypeBkIntegrationId={accessTypeBkIntegrationId}
    >
      {({ initAccessType, getSubscriberMetadata }) => (
        <SignInModalContent
          isOpen={isOpen}
          onClose={onClose}
          getSubscriberMetadata={getSubscriberMetadata}
          initAccessType={initAccessType}
        />
      )}
    </AccessType>
  );
};

export { SignInModal };